/**
 * Encryption Service
 * Handles encryption/decryption of sensitive connection credentials
 */

import CryptoJS from 'crypto-js';

const ENCRYPTION_KEY = process.env.DB_CONNECTOR_ENCRYPTION_KEY || 'default-dev-key-change-in-production';

export class EncryptionService {
  /**
   * Encrypt sensitive data (connection credentials, API keys, etc.)
   */
  static encrypt(data: any): string {
    const jsonString = JSON.stringify(data);
    return CryptoJS.AES.encrypt(jsonString, ENCRYPTION_KEY).toString();
  }

  /**
   * Decrypt previously encrypted data
   */
  static decrypt<T = any>(encryptedData: string): T {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedString) {
      throw new Error('Failed to decrypt data - invalid key or corrupted data');
    }
    
    return JSON.parse(decryptedString) as T;
  }

  /**
   * Hash sensitive data (for logging without exposing actual values)
   */
  static hash(data: string): string {
    return CryptoJS.SHA256(data).toString().substring(0, 16);
  }

  /**
   * Mask sensitive string (show first and last few characters)
   */
  static mask(value: string, showChars: number = 4): string {
    if (!value || value.length <= showChars * 2) {
      return '****';
    }
    const start = value.substring(0, showChars);
    const end = value.substring(value.length - showChars);
    return `${start}****${end}`;
  }

  /**
   * Generate a random secret
   */
  static generateSecret(length: number = 32): string {
    return CryptoJS.lib.WordArray.random(length).toString();
  }
}
